<template>
  <v-row no-gutters class="fill-height">
    <v-col cols="12">
      <card-title-row
        class="pl-0 pt-5"
        translation-path="sidebar.sewerProfile.sewerModelProfile"
        :has-buttons="false"
        has-back-button
        @action="backButtonAction"
      >
      </card-title-row>
      <sidebar-row class="pt-5">
        <base-button-subheader subheader-translation-path="sidebar.sewerProfile.selectLinksSubtitle">
          <dots-menu
            :items="dotsItems"
            @single="turnOnSelecting('single')"
            @multiple="turnOnSelecting('multiple')"
            @freehand="turnOnSelecting('freehand')"
          >
            <template #activator="{ on }">
              <v-badge :content="selectedLinksLength" :value="isAnyLinkSelected" color="error" overlap>
                <base-button
                  v-on="getButtonListeners(on)"
                  @click="turnOffSelecting"
                  class="mr-0"
                  translation-path="sidebar.sewerProfile.selectLinks"
                  :button-style="buttonStyle"
                >
                </base-button>
              </v-badge>
            </template>
          </dots-menu>
        </base-button-subheader>
      </sidebar-row>
      <template v-if="isAnyLinkSelected">
        <sidebar-row class="pt-5 pr-5">
          <v-row no-gutters class="justify-end">
            <v-col cols="auto">
              <clickable-text @click="deleteFeature(null)">
                {{ $i18n.t('sidebar.sewerProfile.deleteAllLinks') }}
              </clickable-text>
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pr-5">
          <v-list class="py-0">
            <template v-for="feature in selectedLinks">
              <v-list-item
                class="hover-list-element"
                @mouseleave="onHover({ id: feature, layerId: 'links' }, false)"
                @mouseenter="onHover({ id: feature, layerId: 'links' }, true)"
                :key="feature"
              >
                <v-list-item-content class="pa-0">
                  <v-list-item-title>
                    <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                      ID: {{ feature }}
                    </v-subheader>
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon @click="deleteFeature(feature)" color="rgba(0,0,0,.7)" size="19px">mdi-close</v-icon>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </sidebar-row>
        <sidebar-row class="pt-5" slot-cols="auto">
          <base-button
            class="mr-0"
            translation-path="sidebar.sewerProfile.generateProfile"
            @click="onOpened"
            :loading="isLoading"
          >
          </base-button>
        </sidebar-row>
      </template>
      <sewer-profile-dialog
        v-if="isDialogLoaded"
        :is-visible.sync="isDialogVisible"
        :pipes="computedSelectedLinks"
        :scenario-id="scenarioId"
        :timesteps="selectedScenarioTimesteps"
        :parent-image="image"
        :image-name="name"
      >
      </sewer-profile-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import identificationSidebarCard from '@/mixins/identificationSidebarCard';

import ClickableText from '@/components/ClickableText';
import CardTitleRow from '@/components/CardTitleRow';
import DotsMenu from '@/components/DotsMenu';
import SidebarRow from '@/components/SidebarRow';

const LINKS_LAYER_NAME = 'links';

export default {
  name: 'SewerProfile',
  mixins: [identificationSidebarCard],
  components: {
    ClickableText,
    SidebarRow,
    DotsMenu,
    CardTitleRow,
    SewerProfileDialog: () => import('@/components/SewerProfileDialog'),
  },
  props: {
    netType: {
      type: String,
      required: true,
    },
    scenarioId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    isDialogLoaded: false,
    isDialogVisible: false,
    isButtonActive: false,
    dotsItems: [
      {
        name: 'single',
      },
      {
        name: 'multiple',
      },
      {
        name: 'freehand',
      },
    ],
    name: undefined,
    image: undefined,
    isLoading: false,
  }),
  computed: {
    activeTool: get('tools/activeTool'),
    selectedLinks: get('map/selectedFeatures@links'),
    computedSelectedLinks() {
      return this.selectedLinks || [];
    },
    isScenarioAdded: get('modeling/isScenarioAdded'),
    isFromProfile: sync('modeling/isFromProfile'),
    selectedScenarioTimesteps: get('modeling/modelsScenarios@:netType@:scenarioId.timesteps'),
    buttonStyle() {
      return this.isButtonActive || this.isButtonMultipleActive
        ? {
            backgroundColor: '#E8F0FE !important',
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
    isAnyLinkSelected() {
      return this.selectedLinksLength > 0;
    },
    selectedLinksLength() {
      return this.computedSelectedLinks.length;
    },
  },
  methods: {
    getProfile: call('modeling/getProfile'),
    getIntersectingLinksAction: call('modeling/getIntersectingLinks'),
    openProfileDialog() {
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    async getIntersectingLinks(geometry) {
      const { scenarioId } = this;
      return await this.getIntersectingLinksAction({ scenarioId, type: LINKS_LAYER_NAME, body: { geometry } });
    },
    turnOnSelecting(type) {
      if (type === 'single') {
        this.$root.$emit('attachOnClickSelection', LINKS_LAYER_NAME, false);
      } else if (type === 'multiple') {
        this.$root.$emit('selection-action', 'multiple', {
          selectionBoxAction: this.getIntersectingLinks,
          layerId: LINKS_LAYER_NAME,
        });
      } else {
        this.$root.$emit('selection-action', 'freehand', {
          selectionBoxAction: this.getIntersectingLinks,
          layerId: LINKS_LAYER_NAME,
        });
      }
      this.$root.$emit('modelingResultsIdentification-action', false);
      this.isButtonActive = true;
    },
    turnOffSelecting() {
      this.$root.$emit('modelingResultsIdentification-action', true);
      this.$root.$emit('selection-action');
      this.isButtonActive = false;
    },
    getButtonListeners(on) {
      return this.isButtonActive
        ? {
            click: this.turnOffSelecting,
          }
        : on;
    },
    backButtonAction() {
      this.isFromProfile = true;
      this.$router.back();
    },
    deleteFeature(id) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [LINKS_LAYER_NAME]: deletedIds,
        },
      });
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'highlitedFeatures',
        features: {
          [LINKS_LAYER_NAME]: deletedIds,
        },
      });
    },
    async onOpened() {
      try {
        this.isLoading = true;
        const { selectedLinks: pipes, scenarioId } = this;
        const r = await this.getProfile({ pipes, scenarioId, index: 0 });
        this.name = this.$_extractFilename(r.headers);
        this.image = this.$_createImageFromArrayBuffer(r.data, r.headers);
        this.openProfileDialog();
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (nV !== oV && oV === 'selection') {
        this.isButtonActive = false;
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (!vm.isScenarioAdded) {
        if (from.name) {
          vm.isFromProfile = false;
          next(false);
        } else {
          vm.isFromProfile = false;
          next({ name: 'modelingResults', params: { ...to.params } });
        }
      } else {
        vm.isFromProfile = true;
        next();
      }
    });
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'modelingResults') {
      this.$root.$emit('removeModelingResultsLayers');
      this.$root.$emit('modelingResultsIdentification-action', false);
    } else {
      this.$root.$emit('modelingResultsIdentification-action', true);
    }
    this.$root.$emit('selection-action');
    next();
  },
};
</script>

<style lang="scss" scoped>
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
::v-deep .flex-wrap {
  flex-wrap: nowrap !important;
}
</style>
